<script lang="ts" setup>
import { RouteLocationRaw } from 'vue-router';
import { IconName } from '@/ui/UiIcon.vue';

type Mode = 'minimal' | 'full';

export type SideNavigationItemProps = {
  icon: IconName;
  label: string;
  mode: Mode;
  to: RouteLocationRaw;
  /**
   * Named routes whose pages, nested routes included, also belong to this
   * item, so it stays active on them and not only on its exact link target.
   */
  activeRoutes?: string[];
};

const props = defineProps<SideNavigationItemProps>();

const route = useRoute();

const isFull = computed(() => {
  return props.mode === 'full';
});

const isSectionActive = computed(() =>
  route.matched.some((record) =>
    props.activeRoutes?.includes(record.name as string),
  ),
);
</script>

<template>
  <router-link
    class="app-side-navigation-item"
    :class="{ 'is-active': isSectionActive }"
    :to="props.to"
    :data-mode="mode"
  >
    <UiIcon :name="icon" class="icon" />
    <Transition name="fade">
      <div v-if="isFull" class="info">
        <span class="label">{{ props.label }}</span>
      </div>
    </Transition>
  </router-link>
</template>

<style lang="scss" scoped>
.app-side-navigation-item {
  display: flex;
  align-items: center;
  padding: var(--space-8);
  transition-property: color, background-color;
  transition-duration: var(--duration-base);
  transition-timing-function: ease-out;
  border-radius: var(--border-radius-6);
  // The list scrolls (.main-items), which would cut an outer ring at its edge.
  @include focus-ring($inset: true);

  &:hover {
    background-color: var(--color-primary-100);
    color: var(--color-primary-800);
    transition-duration: 0s;
  }

  // primary-600: white on it is 5.53:1 (primary-400 was 3.41:1, below AA)
  &.router-link-exact-active,
  &.is-active {
    background-color: var(--color-primary-600);
    color: white;
    --color-focus-ring: var(--color-gray-0);

    // A white ring at the edge would merge with the white panel; a band of the
    // fill stays outside it.
    &:focus-visible {
      outline-offset: calc(
        var(--focus-ring-offset-inset) - var(--focus-ring-width)
      );
    }
  }

  color: var(--color-gray-600);
}

.app-side-navigation-item:not(:last-child) {
  margin-bottom: var(--space-4);
}

.icon {
  width: px(22);
  height: px(22);
  flex: 0 0 auto;
}

.info {
  margin-left: var(--space-8);
  overflow: hidden;

  &.fade-enter-active,
  &.fade-leave-active {
    transition: all var(--duration-base) ease-out;
  }

  &.fade-enter-from,
  &.fade-leave-to {
    opacity: 0;
    transform: translateX(#{px(-24)});
  }
}

.label {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
</style>
